import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { Link, Stack } from '@mui/material';

import { useConfirmDialog } from 'contexts/ConfirmDialogContext';
import {
  createFolder,
  deleteFile,
  deleteQuoteSet,
  downloadFile,
  duplicateFile,
  getFile,
  moveFile,
  renameFile,
  restoreTrashFile,
  updateQuoteSet,
  setActiveFolder,
} from 'store/slices/files';
import { setFileAsPropertyPhoto } from 'store/slices/properties';
import { notistackBinAction } from 'utils/notistack';
import { fileTypeChecks } from 'utils/file-icon';

export const useFile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: propertyId, fileId } = useParams();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { openConfirmDialog } = useConfirmDialog();

  const currentPropertyId = useSelector((state) => state.properties.currentProperty?.id);
  const currentPropertyOid = useSelector((state) => state.properties.currentProperty?.oid);
  const currentUserId = useSelector((state) => state.auth.currentUser?.id);

  const [restoreLoading, setRestoreLoading] = useState(false);

  const handleDeleteFile = (file, callback = () => {}) => {
    if (fileTypeChecks.isFileQuote(file)) {
      openConfirmDialog({
        title: `Delete Quote`,
        text: `You are about to delete a quote. Click 'Delete' to proceed`,
        handler: async () => {
          try {
            await dispatch(deleteQuoteSet(file.oid)).unwrap();

            enqueueSnackbar(`Quote has been successfully deleted.`);
            callback();
          } catch {
            enqueueSnackbar(`Unable to delete quote`, { variant: 'error' });
            return false;
          }
        },
      });
      return;
    }
    openConfirmDialog({
      title: `Delete ${file?.format}`,
      text: `You are about to delete ${file.name}. Click 'Delete' to proceed`,
      handler: async () => {
        try {
          await dispatch(deleteFile(file.oid)).unwrap();
          enqueueSnackbar(
            <span
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >{`${file.name} has been successfully deleted.`}</span>
          );
          callback();
        } catch {
          enqueueSnackbar(`Unable to delete ${file?.format}`, { variant: 'error' });
          return false;
        }
      },
    });
  };

  const handleDownloadFile = (item) => {
    dispatch(downloadFile(item))
      .unwrap()
      .then(() => {
        enqueueSnackbar(
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{`${item.name} download successful.`}</span>
        );
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleDuplicateFile = (item) => {
    const file = {
      ...item,
      parent: item.owner === currentUserId ? item.parent : '',
    };

    dispatch(duplicateFile(file))
      .unwrap()
      .then(() => {
        enqueueSnackbar(
          item.owner === currentUserId ? (
            <span
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >{`${item.name} has been successfully duplicated.`}</span>
          ) : (
            <span
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >{`A copy of ${item.name} has been created in your Files.`}</span>
          )
        );
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleRestoreFile = (file, callback = () => {}) => {
    // route redirection yung overview property
    const getFilePath = async (file) => {
      if (file?.entity === 'Property') {
        return 'properties';
      }
      if (file?.parent_file?.entity === 'Property') {
        return `properties/${file.parent_file.oid}`;
      }

      if (file?.parent && file?.parent_file) {
        let currentId = file.parent;
        const visited = new Set();
        while (currentId) {
          if (visited.has(currentId)) break;
          visited.add(currentId);
          try {
            const parentFile = await dispatch(getFile({ id: currentId })).unwrap();
            if (!parentFile) break;
            if (parentFile.entity === 'Property') {
              return `properties/${parentFile.oid}/files/${file.parent_file.oid}`;
            }
            currentId = parentFile.parent;
          } catch {
            break;
          }
        }
      }

      return file?.parent ? `files/${file.parent}` : 'files';
    };

    setRestoreLoading(true);
    dispatch(restoreTrashFile(file.id))
      .unwrap()
      .then(async (response) => {
        const path = await getFilePath(response.data);
        enqueueSnackbar(`Restored ${file.name}`, {
          action: (snackbarId) => {
            return notistackBinAction({
              id: snackbarId,
              path,
            });
          },
          persist: true,
        });
        callback();
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }))
      .finally(() => setRestoreLoading(false));
  };

  const handleRenameFile = (id, name, fileType, callback = () => {}) => {
    dispatch(renameFile({ id, name }))
      .unwrap()
      .then(() => {
        enqueueSnackbar(`${fileType} has been successfully updated.`);
        callback();
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleCreateFolder = (folderData, parentId, callback = () => {}) => {
    dispatch(createFolder({ form: folderData, parent: parentId }))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Folder has been successfully created.');
        callback();
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleSetFileAsPropertyPhoto = (fileId, parentId) => {
    dispatch(setFileAsPropertyPhoto({ parent: parentId, id: fileId }))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Photo has been set as property photo');
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleGoToMarketplace = (file) => {
    if (currentPropertyId) navigate(`/properties/${currentPropertyOid}/marketplace/${file.oid}`);
    else if (file.overview?.property_id)
      navigate(`/shared/properties/${file.overview.property_id}/marketplace/${file.oid}`);
  };

  const handleEditArtwork = (file) => {
    let newTabUrl;

    if (propertyId) {
      newTabUrl = `/properties/${propertyId}/artwork/${file.oid}`;
    } else if (fileId) {
      newTabUrl = `/files/${fileId}/artwork/${file.oid}`;
    }

    window.open(newTabUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRenameQuote = (id, name, callback = () => {}) => {
    dispatch(updateQuoteSet({ id, name }))
      .unwrap()
      .then(() => {
        enqueueSnackbar(`Quote has been successfully updated.`);
        callback();
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleUndoMove = (active, snackbarKey) => {
    closeSnackbar(snackbarKey);
    dispatch(moveFile({ id: active.data.current.id, new_parent: active.data.current.parent, isUndo: true }))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Action undone');
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  const handleMoveFile = (active, over) => {
    const activeFolder = fileId ? fileId : propertyId ? currentPropertyId || null : null;
    dispatch(setActiveFolder(activeFolder));

    dispatch(
      moveFile({
        id: active.data.current.id,
        isUndo: false,
        ...(over?.data?.current?.id &&
          over.data.current.id !== 'user-files' && {
            new_parent: over.data.current.id,
          }),
      })
    )
      .unwrap()
      .then(() => {
        const snackbarKey = enqueueSnackbar(
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {`${active?.data?.current?.name} has been moved from ${
                active?.data?.current?.parent_file?.name || 'Files'
              } to ${over?.data?.current?.name}`}
            </span>

            <Link
              variant="body2"
              underline="hover"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={() => handleUndoMove(active, snackbarKey)}
            >
              Undo
            </Link>
          </Stack>
        );
      })
      .catch((error) => enqueueSnackbar(error, { variant: 'error' }));
  };

  return {
    handleDeleteFile,
    handleDownloadFile,
    handleDuplicateFile,
    handleRestoreFile,
    handleRenameFile,
    handleCreateFolder,
    handleSetFileAsPropertyPhoto,
    handleGoToMarketplace,
    handleEditArtwork,
    handleRenameQuote,
    handleMoveFile,
    restoreLoading,
  };
};
